// Copyright 2017 Sorama B.V.

import { BindingEngine, CollectionObserver, Disposable, ICollectionObserverSplice } from "aurelia-framework";

/**
 * Custom collection observer, which obervers both iinstance and collection changes.
 */
export class PropertyCollectionObserver implements CollectionObserver, Disposable {
    private propertyName: string;
    private obj: object;
    private bindingEngine: BindingEngine;
    private callback: (changeRecords: Array<ICollectionObserverSplice<unknown>>) => void;

    private propertySubscription: Disposable;
    private collectionSubscription: Disposable;

    constructor(bindingEngine: BindingEngine, obj: object, propertyName: string) {
        this.bindingEngine = bindingEngine;
        this.propertyName = propertyName;
        this.obj = obj;
    }

    public subscribe(callback: (changeRecords: Array<ICollectionObserverSplice<unknown>>) => void): Disposable {
        this.callback = callback;

        if (this.propertySubscription) this.propertySubscription.dispose();

        this.propertySubscription = this.bindingEngine
            .propertyObserver(this.obj, this.propertyName)
            .subscribe(this.propertyCallback);

        this.subscribeCollectionObserver();

        return this;
    }

    public dispose(): void {
        if (this.collectionSubscription) this.collectionSubscription.dispose();

        this.propertySubscription.dispose();
    }

    private propertyCallback = (
        newValue: Array<ICollectionObserverSplice<unknown>>,
        oldValue: Array<ICollectionObserverSplice<unknown>>
    ): void => {
        // Check if reference is new
        if (newValue !== oldValue) {
            this.subscribeCollectionObserver();
        }

        this.callback(newValue);
    };

    private subscribeCollectionObserver(): void {
        //dispose old subscribtion
        if (this.collectionSubscription) this.collectionSubscription.dispose();

        // Create new one, if property exists
        if (
            Object.prototype.hasOwnProperty.call(this.obj, this.propertyName) &&
            this.obj[this.propertyName] &&
            Array.isArray(this.obj[this.propertyName])
        ) {
            this.collectionSubscription = this.bindingEngine
                .collectionObserver(this.obj[this.propertyName])
                .subscribe(this.callback);
        }
    }
}

/**
 * Extension of the Aurelia Binding system API. Adds extra functionality.
 */
export class ExtendedBindingEngine extends BindingEngine {
    /**
     * Creates a collection oberver, which observers both instance and collection changes.
     * @param obj object to observe
     * @param propertyName propertyname to observer, in the corresponding object.
     */
    public propertyCollectionObserver(obj: object, propertyName: string): CollectionObserver {
        return new PropertyCollectionObserver(this, obj, propertyName);
    }
}
